import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import {Transition} from "../assets/assets"

interface FirmwareDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (build: number | null, major: number, minor: number, status: boolean) => void;
  onFileUpload: (file: File) => Promise<void>;
  initialVersion: {
    build: number;
    major: number;
    minor: number;
    status: boolean;
  };
}

const FirmwareUpdateDialog: React.FC<FirmwareDialogProps> = ({
  open,
  onClose,
  onUpdate,
  onFileUpload,
  initialVersion,
}) => {
  const [version, setVersion] = useState(initialVersion);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    setVersion(initialVersion);
    setFile(null);
  }, [initialVersion, open]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      if (selectedFile.name.endsWith(".kcl") || selectedFile.name.endsWith(".hex")) {
        setFile(selectedFile);
      } else {
        alert("Please upload a .hex or .kcl file");
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
  });

  const isVersionChanged = () => {
    const isBuildChanged = version.build !== initialVersion.build;
    const isMajorChanged = version.major !== initialVersion.major;
    const isMinorChanged = version.minor !== initialVersion.minor;
    const isStatusChanged = version.status !== initialVersion.status;
    
    return isBuildChanged || isMajorChanged || isMinorChanged || isStatusChanged;
  };

  const handleCombinedAction = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const buildValue = version.build === initialVersion.build ? null : version.build;
      onUpdate(buildValue, version.major, version.minor, version.status);
      await onFileUpload(file);
      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Upload failed:", error);
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      sx={{ borderRadius: "10px" }}
      PaperProps={{
        sx: {
          boxShadow: 10,
          borderRadius: 3,
          width: 500,
          maxWidth: "100%",
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.61)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",

          color: "white",
          mb: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Firmware Update
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Version Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Build"
              type="number"
              value={version.build}
              onChange={(e) => {
                const value = e.target.value;
                setVersion({ 
                  ...version, 
                  build: value === '' ? 0 : Number(value) 
                });
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Major"
              type="number"
              value={version.major}
              onChange={(e) => setVersion({ ...version, major: Number(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Minor"
              type="number"
              value={version.minor}
              onChange={(e) => setVersion({ ...version, minor: Number(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <FormControlLabel
            control={
              <Switch
                checked={version.status}
                onChange={(e) => setVersion({ ...version, status: e.target.checked })}
                color="primary"
              />
            }
            label="Firmware Status"
          />
        </Box>

        <Box mt={4}>
          <Typography variant="subtitle1" gutterBottom>
            Firmware File (.hex or .kcl)
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "text.secondary",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive ? "action.hover" : "background.paper",
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <Typography>{file.name}</Typography>
            ) : isDragActive ? (
              <Typography>Drop the file here</Typography>
            ) : (
              <Typography>Drag & drop a file here, or click to select</Typography>
            )}
          </Box>

          {isUploading && (
            <Box mt={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Uploading: {uploadProgress}%
              </Typography>
              <CircularProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCombinedAction}
          color="primary"
          variant="contained"
          disabled={!file || !isVersionChanged() || isUploading}
        >
          {isUploading ? "Uploading..." : "Update firmware"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FirmwareUpdateDialog;